import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Typography, Tag, Descriptions, Table, Button, Space, Modal, Input, InputNumber, Select, Image, Spinner, Result, Tooltip, Row, Col, Timeline, message, EntityAvatar,
} from '../../ui';
import { Check, X, Undo2, Send, Wallet, HandCoins, ArrowRight, CalendarDays, Route, Coins, MapPin } from 'lucide-react';
import { usePermissionChecker, useAuthStore } from '../../store/auth';
import { RoleBadge } from '../../components/RoleBadge';
import { request } from '../../api/client';
import dayjs from 'dayjs';
import { formatIst, formatIstDate, toIstDateString } from '../../lib/time';
import { EditReportModal, EditTab } from './EditReportModal';
import { DayExpenseModal } from './DayExpenseModal';
import { PayReimbursementDialog } from './PayReimbursementDialog';
import { SettlementApproveDialog } from './SettlementApproveDialog';
import { RecordRecoveryDialog } from './RecordRecoveryDialog';
import { WalletTopUpDialog } from './WalletTopUpDialog';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { inr2 } from '../../lib/format';
import { PALETTE } from '../../lib/palette';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Report-level status chip colours.
const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'blue',
  APPROVED: 'cyan',
  CLOSED: 'green',
  CORRECTION_REQUIRED: 'orange',
  REJECTED: 'red',
};

// Per-line verification status chip colours.
export const VERIFY_COLOR: Record<string, string> = {
  PENDING: 'default',
  VERIFIED: 'green',
  MISSING: 'red',
  AMOUNT_MISMATCH: 'orange',
};

const REJECTED_STATUSES = ['REJECTED'];

/*
 * Until 2026-09-11 a travel line saved with no description got one manufactured from the rate card,
 * so the Description cell repeated the Travel cell beside it word for word — "Car · 20 km @ ₹9/km"
 * next to "CAR · 20 km @ ₹9.00/km". The server no longer writes one, but claims saved before that
 * still carry it (13 of 19 travel lines on this dev copy), and an approver should not read the same
 * figures twice. The two renderings differ in case, spacing, the ₹ and a trailing .00, so compare on
 * a normalised form rather than by equality.
 */
export const isTravelEcho = (text: string, r: any) => {
  if (!r?.travelMode) return false;
  const norm = (x: string) => x.toLowerCase().replace(/\.00\b/g, '').replace(/[^a-z0-9@/]/g, '');
  return norm(text) === norm(`${r.travelMode} ${Number(r.distanceKm)} km @ ${Number(r.ratePerKm)}/km`);
};

const FACT_TONE = {
  brand: 'bg-brand-500/10 text-brand-700 dark:text-brand-300',
  sky: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  gold: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300',
  violet: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
  muted: 'bg-surface-3 text-subtle',
} as const;

/** One fact of the field day as a tile — icon chip, label, value, and the sentence that explains it. */
function DayFact({ icon, tone, label, value, hint }: {
  icon: ReactNode; tone: keyof typeof FACT_TONE; label: string; value: ReactNode; hint?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-card border border-line bg-surface-2 px-3 py-2.5">
      <span className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-control ${FACT_TONE[tone]}`}>{icon}</span>
      <div className="min-w-0">
        <div className="type-overline text-subtle">{label}</div>
        <div className="mt-0.5 type-body-strong">{value}</div>
        {hint && <div className="mt-0.5 type-caption text-muted">{hint}</div>}
      </div>
    </div>
  );
}

/** One row of the invoice-style money stack (label left, amount right-aligned). */
function AmountRow({ label, value, tone, strong }: {
  label: ReactNode; value: ReactNode; tone?: 'danger' | 'brand'; strong?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between gap-4 ${strong ? 'py-1' : 'py-1.5'}`}>
      <span className={strong ? 'text-sm font-semibold text-content' : 'text-sm text-muted'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'type-metric-sm text-content' : `text-sm ${tone === 'danger' ? 'text-danger-600 dark:text-danger-500' : tone === 'brand' ? 'text-brand-700 dark:text-brand-300' : 'text-content'}`}`}>
        {value}
      </span>
    </div>
  );
}

export function ExpenseReportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const myId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', id],
    queryFn: () => request<any>({ url: `/expense-reports/${id}` }),
    // Approval state is sensitive — always re-fetch the live status on open so we
    // never show stale action buttons for an already-progressed report.
    refetchOnMount: 'always',
  });

  // Generic PATCH action against this report (submit / approve / reject / …).
  const action = useConfirmedMutation((v) => {
      const asked: Record<string, any> = {
        submit: { title: 'Submit this claim for approval?', content: 'It goes for approval next, and you can’t edit it after submitting.', okText: 'Yes, submit' },
        approve: { title: 'Approve this claim?', content: 'The company accepts the approved amount. Their float is used first; Accounts pays them only what it does not cover.', okText: 'Yes, approve' },
        reject: { title: 'Reject this claim?', content: 'Nothing is reimbursed, and your reason goes back to the rep.', okText: 'Yes, reject', danger: true },
        reopen: { title: 'Reopen this claim?', content: 'It goes back to the rep as a draft. What it took from their wallet and collection cash is put back, and a reimbursement already paid becomes money they owe back.', okText: 'Yes, reopen', danger: true },
        close: { title: 'Settle this claim?', content: 'The reimbursement is recorded as paid and the claim is closed.', okText: 'Yes, settle' },
      };
      return asked[v.url] ?? { title: 'Go ahead with this?', okText: 'Yes' };
    }, {
    mutationFn: ({ url, data }: { url: string; data?: any }) =>
      request<any>({ url: `/expense-reports/${id}/${url}`, method: 'PATCH', data }),
    onSuccess: (res: any, vars) => {
      // Approval closes a claim the float or collected cash already met — say so, not just "complete".
      // Said in the reader's words — never "Action \"reject\" complete".
      const done: Record<string, string> = {
        submit: 'Claim submitted for approval',
        approve: res?.settled ? 'Approved and settled — nothing to pay' : 'Approved — waiting for Accounts to pay the rest',
        reject: 'Claim rejected',
        'send-back': `Sent back to ${report?.repName ?? 'the rep'}`,
        reopen: Number(res?.recoveryDue ?? 0) > 0
          ? `Claim reopened — ${inr2(res.recoveryDue)} already reimbursed is now owed back by ${report?.repName ?? 'the rep'}`
          : 'Claim reopened as a draft',
        close: 'Claim settled',
      };
      message.success(done[vars.url] ?? 'Saved');
      qc.invalidateQueries({ queryKey: ['expense-report', id] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // Per-line verification.
  const verifyItem = useConfirmedMutation({ title: "Save this expense’s verification?", content: "It sets what will actually be reimbursed for this expense.", okText: "Yes, save" }, {
    mutationFn: (vars: { itemId: string; data: any }) =>
      request({ url: `/expense-reports/${id}/items/${vars.itemId}/verify`, method: 'PATCH', data: vars.data }),
    onSuccess: () => {
      message.success('Expense updated');
      qc.invalidateQueries({ queryKey: ['expense-report', id] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
      setMismatchItem(null);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // ── Modal state for reason / amount inputs ──
  const [reasonModal, setReasonModal] = useState<{ url: string; title: string; field: string } | null>(null);
  const [reason, setReason] = useState('');

  // Recording the actual reimbursement (Accounts) — the same dialog the register's row action opens.
  const [payOpen, setPayOpen] = useState(false);
  // Recording money a rep owes back on a reversed, already-reimbursed claim.
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  // Giving the rep new float with this claim in view — a separate payment from any reimbursement.
  const [topUpOpen, setTopUpOpen] = useState(false);

  const [adminOpen, setAdminOpen] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState<number | null>(null);
  const [adminRemarks, setAdminRemarks] = useState('');

  // Mismatch verify needs a verified amount.
  const [mismatchItem, setMismatchItem] = useState<any | null>(null);
  const [verifiedAmount, setVerifiedAmount] = useState<number | null>(null);
  const [mismatchReason, setMismatchReason] = useState('');
  const [editTab, setEditTab] = useState<EditTab | null>(null);

  // Honor "?edit=1" from a deep link — open the editor once the report loads, but only for
  // someone allowed to edit (owner / filer, while draft / sent back).
  useEffect(() => {
    if (!report || searchParams.get('edit') !== '1') return;
    const owner = String(report.repId) === String(myId);
    const creator = String(report.createdBy ?? '') === String(myId);
    if ((owner || creator) && ['DRAFT', 'CORRECTION_REQUIRED'].includes(report.status)) setEditTab('expenses');
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [report, searchParams, myId, setSearchParams]);

  // Seed the approve amount from the claim whenever the dialog opens, not just in the button's click
  // handler — so it is prefilled however the dialog came to be open (and survives a hot reload that
  // re-renders an already-open dialog).
  useEffect(() => {
    if (adminOpen && report) setApprovedAmount(Number(report.subtotalAmount ?? 0));
  }, [adminOpen, report]);

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !report) {
    return (
      <Result
        status="error"
        title="Couldn't load this report"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/expense-reports')}>Back to reports</Button>}
      />
    );
  }

  const status: string = report.status;
  const isOwner = String(report.repId) === String(myId);
  // The manager/admin who filed on a rep's behalf isn't the owner, but still owns the draft.
  const isCreator = String(report.createdBy ?? '') === String(myId);
  const isFiler = isOwner || isCreator;

  // Permission + status gating (mirrors the backend guards).
  const isApprover = hasPerm('EXPENSE.APPROVE.HEAD') || hasPerm('EXPENSE.APPROVE.ACCT');
  const canSubmit = isFiler && (status === 'DRAFT' || status === 'CORRECTION_REQUIRED');
  // Single approval layer: any approver approves / rejects / sends back a SUBMITTED report.
  // NOBODY DECIDES THEIR OWN CLAIM. A Sales Head holds approval permission for their team, so a bare
  // permission check put Approve / Reject / Send back — and the per-expense verify buttons — on the Sales
  // Head's OWN claim. The server refuses every one of those on your own claim (SELF_ACTION); the page must
  // not offer them. Their own claim goes to Admin / Accounts / Super Admin.
  // …nor one they FILED for somebody else: the server refuses it (FILED_ON_BEHALF), another approver decides.
  const canReview = status === 'SUBMITTED' && isApprover && !isOwner && !isCreator;
  // Reopen is an Admin / Super Admin override only — not a routine accounts action.
  const canReopen = (REJECTED_STATUSES.includes(status) || status === 'CLOSED') && (role === 'ADMIN' || role === 'SUPER_ADMIN') && !isOwner;
  // The sales person (owner) or whoever filed it edits the report only while it's a
  // draft / sent back — no active wallet debit exists in those states, so it's safe.
  const canEdit = isFiler && (status === 'DRAFT' || status === 'CORRECTION_REQUIRED');
  const canEditItems = canEdit;
  // Per-line verify is open to any approver while the report is in review.
  const canVerifyLines =
    status === 'SUBMITTED' && !isOwner && !isCreator &&
    (hasPerm('EXPENSE.APPROVE.HEAD') || hasPerm('EXPENSE.APPROVE.ACCT'));

  const items: any[] = report.items ?? [];
  // Expenses whose required proof is not attached — the server's own `proofRequired` flag per expense.
  const missingProof = items.filter((i) => i.proofRequired && !i.billUrl);
  // A day claim covers a whole field day, so it has no single party or exit/return time — those header
  // fields belong to the older per-visit report and would render as a wall of dashes here.
  const isDayClaim = !!report.expenseDate && !report.visitId;
  // How many IST calendar days after the field day the claim was created. It is the one thing the
  // two dates say together — an approver checks a claim written up a week later more carefully — so
  // the Day tile states it, in words, only when it is not the same day.
  const createdDaysLater = report.expenseDate && report.createdAt
    ? dayjs(toIstDateString(report.createdAt)).diff(dayjs(toIstDateString(report.expenseDate)), 'day')
    : 0;
  const dayVisitCount = new Set(items.map((i) => i.visitId).filter(Boolean)).size;
  /* The visits the rep actually reached that day, from the visits themselves (server) — the tile used to show only
     the count of expenses tagged to a visit, which read "—" on a day with three calls. */
  const dayVisitsMade = Number(report.dayVisits?.reached ?? 0);

  // Settlement comes from the SERVER — the same arithmetic it will commit — so the figure Accounts
  // approves is the figure that gets paid. Null on a rejected claim: nothing settled, which is not ₹0.
  const settlement = report.settlement ?? null;
  /* Which book pays. Everything money-facing on this page reads from here rather than assuming the
     wallet, because a cash-funded claim never touches it. */
  const cashFunded = String(report.fundingSource ?? 'WALLET').toUpperCase() === 'CASH_CUSTODY';
  const due = Number(settlement?.reimbursementDue ?? 0);
  /* SETTLEMENT V2 — the split frozen at approval, and the reimbursement it left (if any). */
  const v2 = report.settlementV2 ?? null;
  const v2Owed = v2?.reimbursement?.status === 'OUTSTANDING' ? Number(v2.reimbursement.amount) : null;
  /* MONEY OWED BACK — a reimbursement already paid on this claim before it was reopened or sent back. */
  const recoveries: any[] = report.recoveries ?? [];
  const owedBack = recoveries.filter((r) => r.status === 'OUTSTANDING').reduce((n, r) => n + Number(r.amount ?? 0), 0);
  /* Under the V2 rule nothing touches the wallet until approval, so an open or reversed claim must not be described
     with the old "submitting debits the wallet" wording — that text belongs to claims the old rule actually settled. */
  const openUnderV2 = !v2 && report.settlementRule === 'V2'
    && !(report.settlementVersion == null && ['APPROVED', 'CLOSED'].includes(status));
  /** How much the approver cut off the claim — a fact the money stack has to name, or "Approved" just
   *  silently disagrees with "Claimed". */
  const trimmed = report.paidAmount == null
    ? 0
    : Math.max(0, Number(report.subtotalAmount ?? 0) - Number(report.paidAmount));
  /** "Asmita Kale’s" — or a grammatical fallback when the name did not resolve. */
  const whose = report.repName ? `${report.repName}’s` : 'their';

  const setVerify = (itemId: string, vStatus: string) =>
    verifyItem.mutate({ itemId, data: { status: vStatus } });

  /** Does any line on this claim actually carry this field? Drives which optional columns are shown. */
  const hasLineValue = (key: string) => items.some((i) => i[key] != null && String(i[key]).trim() !== '');

  const lineColumns: any[] = [
    { title: 'Date', dataIndex: 'expenseDate', width: 90, render: (v: any) => formatIst(v, 'DD MMM YYYY') },
    {
      title: 'Category', dataIndex: 'category', width: 90,
      render: (v: string, r: any) => (
        <Space size={4} wrap>
          <Tag>{v}</Tag>
          {/* System lines (the daily allowance) are computed, not entered — say so, so an approver
              knows nobody typed the number. `!!` matters: isSystem arrives from MySQL as 0/1, and a
              bare `0 &&` renders the zero. */}
          {!!r.isSystem && <Tag color="blue">auto</Tag>}
        </Space>
      ),
    },
    {
      // How the line was priced: a per-km line shows its mode, distance and the rate that was frozen
      // onto it, which is what an approver needs to check the arithmetic.
      title: 'Travel', dataIndex: 'travelMode', width: 120,
      render: (mode: string, r: any) => (mode
        ? <span className="whitespace-nowrap">{mode} · {Number(r.distanceKm)} km @ {inr2(r.ratePerKm)}/km</span>
        : <span className="text-muted">—</span>),
    },
    {
      // The substance an approver actually reads — "Pune → Nashik dealer tour, own car (odometer
      // 44,120 → 44,240)". It WRAPS now. `ellipsis` here resolves to `max-w-[1px] truncate`, which
      // under table-layout:auto squeezes the column to nothing: this was cut at "Car · 20 km @ ₹…"
      // with no tooltip, no expand and nothing to click, so the text was simply unreachable.
      title: 'Description', dataIndex: 'description', width: 170,
      render: (v: string, r: any) => {
        const text = String(v ?? '').trim();
        if (!text || isTravelEcho(text, r)) return <span className="text-muted">—</span>;
        return <span className="block whitespace-pre-wrap break-words">{text}</span>;
      },
    },
    // Vendor / bill no. / payment mode only mean anything for a BILLED expense — who was paid, their
    // invoice number, and how they were paid. A per-km travel line has none of them (nothing to pay a
    // vendor for), and the day-expense form never asks for them. So these columns appear only when a
    // line on this claim actually carries the value, instead of three permanent columns of dashes.
    ...(hasLineValue('vendorName') ? [{ title: 'Vendor', dataIndex: 'vendorName', width: 130, render: (v: any) => v || '—' }] : []),
    ...(hasLineValue('billNumber') ? [{ title: 'Bill #', dataIndex: 'billNumber', width: 110, render: (v: any) => v || '—' }] : []),
    ...(hasLineValue('paymentMode') ? [{ title: 'Paid by', dataIndex: 'paymentMode', width: 110, render: (v: any) => (v ? <Tag>{v}</Tag> : '—') }] : []),
    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, width: 80, render: (v: any) => inr2(v) },
    {
      // The receipt/bill image attached to this line. NOT every line has one — a per-km travel line is
      // priced from the rate card, not from a bill — so an empty cell is normal and must not be dressed
      // as an error. (It used to render a red chip on every line without an attachment.)
      title: 'Proof',
      dataIndex: 'billUrl',
      width: 90,
      render: (url: string, r: any) =>
        url
          ? (/\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url)
            /* A small thumbnail that opens the preview in the page — the same cell the approve dialog draws.
               Upload URLs often carry no file extension, which is why the extension test fell through to a
               bare "View" link; AttachmentCell works out what the file is and previews it either way. */
            ? <AttachmentCell files={[url]} label={`${r.proofLabel ?? 'Proof'} · ${String(r.category ?? '').replace(/_/g, ' ').toLowerCase()}`} size={36} />
            : <AttachmentCell files={[url]} label={`${r.proofLabel ?? 'Proof'} · ${String(r.category ?? '').replace(/_/g, ' ').toLowerCase()}`} size={36} />)
          // Required and missing is a fault the approver must see (approval is refused); otherwise an empty
          // cell is normal — a daily allowance has nothing to attach.
          : r.proofRequired
            ? <span className="font-medium text-danger-600 dark:text-danger-500">{r.proofLabel ?? 'Proof'} missing</span>
            : <span className="text-muted">Not needed</span>,
    },
    {
      title: 'Verification',
      key: 'verification',
      width: 110,
      render: (_: any, r: any) => (
        // A plain flex column, NOT ui Space: a vertical Space is items-stretch, which blew the status
        // chip out to the full column width. `align="start"` doesn't fix it (the prop never maps
        // 'start'), and a className can't either — `cn` has no tailwind-merge, so items-stretch still
        // wins on CSS order. See the note in ui/Space.
        <div className="flex flex-col items-start gap-0.5">
          {/* "VERIFIED" beside "Odometer / fuel bill missing" was two opposite statements about one expense.
              Marked verified before proof was required, it is said as exactly that. */}
          <Tag color={r.verificationStatus === 'VERIFIED' && r.proofRequired && !r.billUrl ? 'orange' : (VERIFY_COLOR[r.verificationStatus] ?? 'default')}>
            {(r.verificationStatus ?? 'PENDING').replace(/_/g, ' ')}
          </Tag>
          {r.verificationStatus === 'VERIFIED' && r.proofRequired && !r.billUrl && (
            <span className="type-caption text-warning-600 dark:text-harvest-300">but no proof is attached</span>
          )}
          {r.verificationStatus === 'AMOUNT_MISMATCH' && r.verifiedAmount != null && (
            <span style={{ fontSize: 12, color: '#888' }}>Verified {inr2(r.verifiedAmount)}</span>
          )}
          {r.rejectionReason && (
            <span style={{ fontSize: 12, color: 'var(--color-danger-500)' }}>{r.rejectionReason}</span>
          )}
        </div>
      ),
    },
    ...(canVerifyLines ? [{
      title: 'Verify',
      key: 'verifyAction',
      width: 150,
      align: 'right' as const,
      /* ONLY THE ACTIONS THAT CAN CHANGE THIS EXPENSE'S STATE.
         - Proof required and missing: nothing to verify the expense or its amount against, so the only
           action is "Bill missing" (the server refuses the other two).
         - The button for the state it is ALREADY in is not offered: no "Mark verified" on a verified
           expense, no "Bill missing" on one already marked missing. */
      render: (_: any, r: any) => {
        const status = r.verificationStatus ?? 'PENDING';
        const noProof = !!r.proofRequired && !r.billUrl;
        return (
        // Right-aligned under its "Verify" heading, however many of the three buttons apply. A ui Space
        // lays its children out from the left, so a lone button sat under "Verification" instead.
        <div className="flex flex-wrap items-center justify-end gap-1">
          {!noProof && status !== 'VERIFIED' && (
          <Tooltip label="Mark verified">
            <Button
              size="sm" variant="ghost" aria-label="Mark verified" icon={<Check size={16} />}
              onClick={() => setVerify(String(r.id), 'VERIFIED')}
              loading={verifyItem.isPending && verifyItem.variables?.itemId === String(r.id)}
            />
          </Tooltip>
          )}
          {status !== 'MISSING' && (
          <Tooltip label="Bill missing">
            <Button size="sm" variant="danger" aria-label="Bill missing" icon={<X size={16} />} onClick={() => setVerify(String(r.id), 'MISSING')} />
          </Tooltip>
          )}
          {!noProof && (
          <Button
            size="sm"
            onClick={() => {
              setMismatchItem(r);
              setVerifiedAmount(Number(r.verifiedAmount) || Number(r.amount) || null);
              setMismatchReason(r.rejectionReason ?? '');
            }}
          >
            {status === 'AMOUNT_MISMATCH' ? 'Change mismatch' : 'Mismatch'}
          </Button>
          )}
        </div>
        );
      },
    }] : []),
  ];

  // Decisions: each step renders done (name + time) or a greyed "pending".
  const dPending = <Typography.Text type="secondary" italic>pending</Typography.Text>;
  // `color: string` is explicit: PALETTE is `as const`, so inferring from the default would narrow
  // the parameter to the literal '#339022' and reject every other palette value.
  // The role rides along with the name: "Approved by Vikram RSM" alone left the reader to remember
  // who Vikram is, when the one thing a decision row has to say is by WHAT AUTHORITY it was made.
  const dDone = (name?: string, at?: string, color: string = PALETTE.brand[600], label = '', role?: string | null) => (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span style={{ color }}>{label}{name ? `${label ? ' ' : ''}${name}` : ''}</span>
      {role && <RoleBadge role={role} />}
      {at && <span style={{ color: '#888', fontSize: 12 }}>{formatIst(at)}</span>}
    </span>
  );
  const submittedCell = report.submittedAt ? dDone(report.submittedByName, report.submittedAt, PALETTE.sky[500], '', report.submittedByRole) : dPending;
  // Single approval layer — one decision row (approved or rejected by whoever acted).
  const approvedCell = report.adminDecisionAt
    ? dDone(report.approvedByName ?? report.adminDecisionByName, report.adminDecisionAt, status === 'REJECTED' ? PALETTE.danger[500] : PALETTE.brand[600], status === 'REJECTED' ? 'Rejected by' : 'Approved by', report.approvedByRole ?? report.adminDecisionByRole)
    : dPending;

  // Activity timeline — milestones derived from the report's own timestamps.
  const tinfo = (s?: string) => <span style={{ color: '#888', fontSize: 12 }}>{s}</span>;
  // One person on the timeline: their name, their role as the app's coloured pill, then when they
  // acted. Bare names made every milestone look alike — the pill says whether a rep, their Sales
  // Head or Accounts did this, which on an approval is the whole point of the line.
  const tactor = (name?: string | null, role?: string | null, at?: string | null) => (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 align-middle">
      {name ? tinfo(name) : null}
      {role ? <RoleBadge role={role} /> : null}
      {at ? tinfo(name || role ? `· ${formatIst(at)}` : formatIst(at)) : null}
    </span>
  );
  const activity: { color?: string; children: any }[] = [];
  /*
   * THE CLAIM'S WHOLE HISTORY, from the activity log (`report.timeline`). Rebuilt from the row's own timestamps, a claim
   * sent back and resubmitted lost its first submission, the send-back and the approver's reason the moment it was
   * resubmitted — the row keeps only the latest of each; the log keeps every one. The timestamp milestones remain only
   * for a claim with nothing logged.
   */
  const timeline: any[] = Array.isArray(report.timeline) ? report.timeline : [];
  const TIMELINE_COLOR: Record<string, string> = {
    SUBMITTED: 'blue', RESUBMITTED: 'blue', SENT_BACK: 'orange', REVERT_REQUESTED: 'orange',
    APPROVED: 'green', PAID: 'green', CLOSED: 'green', RECOVERY_RECEIVED: 'green',
    REJECTED: 'red', REOPENED: 'red', REVERTED: 'red', DELETED: 'red',
  };
  // Events whose logged sentence adds something the label does not (what changed, what the approval settled, how it was paid).
  const TIMELINE_DETAIL = new Set(['EDITED', 'VERIFIED', 'APPROVED', 'PAID', 'RECOVERY_RECEIVED', 'REVERTED', 'OTHER']);
  if (timeline.length) {
    for (const e of timeline) {
      activity.push({
        color: TIMELINE_COLOR[e.kind] ?? 'gray',
        children: (
          <span>
            <b>{e.label}</b>{' '}{tactor(e.actorName, e.actorRole, e.at)}
            {e.reason && (
              <div className="mt-0.5 text-sm text-content">{e.kind === 'SENT_BACK' ? 'What needs fixing: ' : 'Reason: '}{e.reason}</div>
            )}
            {e.remarks && <div style={{ fontSize: 12 }}>{e.remarks}</div>}
            {TIMELINE_DETAIL.has(e.kind) && e.message && <div style={{ fontSize: 12, color: '#888' }}>{e.message}</div>}
          </span>
        ),
      });
    }
  } else {
    if (report.createdAt) activity.push({ color: 'gray', children: <span><b>Created</b><br />{tactor(report.repName, report.repRole, report.createdAt)}</span> });
    if (report.submittedAt) activity.push({ color: 'blue', children: <span><b>Submitted</b> {tactor(report.submittedByName, report.submittedByRole, report.submittedAt)}</span> });
    if (status === 'CORRECTION_REQUIRED' && report.correctionReason) activity.push({ color: 'orange', children: <span><b>Sent back</b> <span style={{ fontSize: 12 }}>{report.correctionReason}</span></span> });
    if (report.adminDecisionAt) activity.push({ color: status === 'REJECTED' ? 'red' : 'green', children: <span><b>{status === 'REJECTED' ? 'Rejected' : 'Approved'}</b> {tactor(report.approvedByName ?? report.adminDecisionByName, report.approvedByRole ?? report.adminDecisionByRole, report.adminDecisionAt)}{report.adminRemarks && <div style={{ fontSize: 12 }}>{report.adminRemarks}</div>}</span> });
    if (status === 'CLOSED') activity.push({ color: 'green', children: <span><b>Closed</b></span> });
  }

  return (
    <div>
      {/* No Back button here — the app shell renders one on every non-home route (see HIDE_BACK in
          AppShellModern), so a second one on the page just stacks under it. */}

      {/* Header: WHOSE claim and WHAT STATE on the left, every action on the right. The actions used to
          sit on their own row under the title while the person's name sat far away in the opposite
          corner — so the eye crossed the card twice to answer "whose claim is this, and what can I do
          with it". */}
      <Card style={{ marginBottom: 16 }}>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            <EntityAvatar name={report.repName ?? `#${report.repId}`} src={report.repPhoto} size={44} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Typography.Title level={4} style={{ margin: 0 }}>{report.reportNo}</Typography.Title>
                <Tag color={STATUS_COLOR[status] ?? 'default'} style={{ fontSize: 14 }}>{(status ?? '').replace(/_/g, ' ')}</Tag>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 type-caption text-muted">
                <b className="text-content">{report.repName ?? `#${report.repId}`}</b>
                {/* Whose claim this is, and what they are — a name alone leaves a reader guessing
                    whether they are looking at a rep's claim or a Sales Head's. */}
                <RoleBadge role={report.repRole} />
                {/* A day claim has no visit type — printing an em-dash for it just added a stray mark. */}
                {report.visitType && <span>· {String(report.visitType).replace(/_/g, ' ')}</span>}
                {/* No day and no filing time here. "field day 07 Sep 2026 · filed 07 Sep 2026, 01:44 PM"
                    read as one date printed twice — and both were already on the screen: the Day tile
                    carries the day (and says when the claim was created, when that was a later day), and
                    Activity carries the Created time. A pre-day-model report has no Day tile, so it keeps
                    its day here. */}
                {report.expenseDate && !isDayClaim && <span>· field day {formatIstDate(report.expenseDate)}</span>}
              </div>
            </div>
          </div>

          <Space wrap>
          {canSubmit && (
            <Button variant="primary" icon={<Send size={16} />} loading={action.isPending}
              onClick={() => action.mutate({ url: 'submit' })}>Submit</Button>
          )}
          {canReview && (
            <>
              {/* Prefill with what was claimed — the approver is confirming an amount, not filling in a
                  blank. They only touch it to approve LESS than claimed. */}
              <Button variant="primary" icon={<Check size={16} />} onClick={() => { setApprovedAmount(Number(report.subtotalAmount ?? 0)); setAdminRemarks(''); setAdminOpen(true); }}>Approve</Button>
              <Button variant="danger" icon={<X size={16} />} onClick={() => { setReason(''); setReasonModal({ url: 'reject', title: 'Reject report', field: 'reason' }); }}>Reject</Button>
              <Button icon={<Undo2 size={16} />} onClick={() => { setReason(''); setReasonModal({ url: 'send-back', title: 'Send back for correction', field: 'reason' }); }}>Send back</Button>
            </>
          )}
          {/* Approval is the decision; THIS is the settlement. The label states the actual shortfall —
              never the whole approved amount — because an advance the rep already holds settles first.
              With nothing left to pay there is no payment to make, only a claim to close. */}
          {/* Never on your own claim: settling it yourself is the one step where cash leaves, and the
              backend refuses it with SELF_ACTION — so don't offer a payment dialog that cannot succeed. */}
          {status === 'APPROVED' && hasPerm('EXPENSE.APPROVE.ACCT') && !isOwner && (
            <Button variant="primary" icon={<Check size={16} />} onClick={() => setPayOpen(true)}>
              {v2Owed != null ? `Pay reimbursement ${inr2(v2Owed)}` : due > 0 ? `Pay reimbursement ${inr2(due)}` : 'Close as settled'}
            </Button>
          )}
          {/* Money owed back on a reversed, already-reimbursed claim — recorded by Accounts, never by the rep who owes it. */}
          {owedBack > 0 && hasPerm('EXPENSE.APPROVE.ACCT') && !isOwner && (
            <Button variant="primary" icon={<HandCoins size={16} />} onClick={() => setRecoveryOpen(true)}>
              Record {inr2(owedBack)} received
            </Button>
          )}
          {/* New float for the rep, given with this claim in view — a separate payment from any reimbursement. */}
          {report.settlementVersion === 'V2' && ['APPROVED', 'CLOSED'].includes(status) && hasPerm('EXPENSE.APPROVE.ACCT') && !isOwner && (
            <Button icon={<Wallet size={16} />} onClick={() => setTopUpOpen(true)}>Top up wallet</Button>
          )}
          {/* Reopening a CLOSED claim claws back money that has already been paid out, so it must not
              look like the neutral "Send back". Soft-danger: unmistakably a reversal, without shouting
              over the primary action beside it. */}
          {canReopen && (
            <Button variant="soft-danger" icon={<Undo2 size={16} />}
              onClick={() => { setReason(''); setReasonModal({ url: 'reopen', title: 'Reopen report', field: 'reason' }); }}>Reopen</Button>
          )}
          </Space>
        </div>
      </Card>

      {/* SENT BACK TO THE REP: the reason, and the one action that answers it — never buried in Activity. */}
      {status === 'CORRECTION_REQUIRED' && isFiler && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card border border-warning-500/40 bg-warning-500/10 px-4 py-3 text-sm text-warning-600 dark:text-harvest-300">
          <div className="min-w-0 flex-1">
            <b>Sent back for correction.</b> {report.correctionReason}
            {missingProof.length > 0 && <div className="mt-0.5">{missingProof.length} {missingProof.length === 1 ? 'expense still needs' : 'expenses still need'} proof before you can submit again.</div>}
          </div>
          <Button variant="primary" size="sm" onClick={() => setEditTab('expenses')}>
            {missingProof.length > 0 ? 'Attach proof' : 'Fix and resubmit'}
          </Button>
        </div>
      )}

      <Row gutter={16}>
        {/* Left: site, visit, items */}
        <Col xs={24} lg={15}>
          {/* A DAY claim covers a whole field day across however many visits, so it has no single party
              and no one exit/return time — those header fields belong to the old per-visit report and
              are meaningless (and empty) here. Show the day instead: how far it went, what allowance
              that earned, and which visits its lines touch. A pre-day-model report still shows the
              visit header it was actually filed with. */}
          {isDayClaim ? (
            /* FOUR FACTS, ACROSS the card rather than down its left edge. As a one-column
               Descriptions this was a narrow ribbon of text with two thirds of a wide card empty
               beside it; the same four facts as tiles fill the width and can be read in one sweep. */
            <Card title="Field day" style={{ marginBottom: 16 }}>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <DayFact
                  icon={<CalendarDays size={15} />} tone="brand" label="Day"
                  value={formatIstDate(report.expenseDate)}
                  hint={dayjs(report.expenseDate).isValid()
                    ? `${dayjs(report.expenseDate).format('dddd')}${createdDaysLater === 1 ? ' · claim created the next day' : createdDaysLater > 1 ? ` · claim created ${createdDaysLater} days later` : ''}`
                    : undefined}
                />
                <DayFact
                  icon={<Route size={15} />} tone="sky" label="Distance claimed"
                  value={Number(report.totalDistanceKm) > 0 ? `${Number(report.totalDistanceKm)} km` : '—'}
                  hint={Number(report.totalDistanceKm) > 0 ? 'sum of the day’s travel entries' : 'no travel entry on this claim'}
                />
                <DayFact
                  icon={<Coins size={15} />} tone={Number(report.daAmount) > 0 ? 'gold' : 'muted'} label="Daily allowance"
                  value={Number(report.daAmount) > 0 ? inr2(report.daAmount) : 'None'}
                  hint={Number(report.daAmount) > 0 ? 'earned by the day’s distance' : 'the day’s distance is below the threshold'}
                />
                <DayFact
                  icon={<MapPin size={15} />} tone={(report.dayVisits ? dayVisitsMade : dayVisitCount) > 0 ? 'violet' : 'muted'} label="Visits this day"
                  value={report.dayVisits ? String(dayVisitsMade) : (dayVisitCount > 0 ? String(dayVisitCount) : '—')}
                  hint={report.dayVisits
                    ? `${dayVisitsMade === 0 ? 'no visit reached that day' : `${report.dayVisits.completed} of ${dayVisitsMade} completed`} · ${dayVisitCount > 0 ? `${dayVisitCount} tagged on expenses` : 'none tagged on expenses'}`
                    : (dayVisitCount > 0 ? 'tagged on the claim’s expenses' : 'costs recorded for the day as a whole')}
                />
              </div>
              {report.notes && (
                <div className="mt-3 rounded-control border border-line bg-surface-2 px-3 py-2">
                  <div className="type-overline text-subtle">Notes</div>
                  <div className="mt-0.5 text-sm">{report.notes}</div>
                </div>
              )}
            </Card>
          ) : (
            <>
              <Card title="Site visited" style={{ marginBottom: 16 }}
                extra={canEdit ? <Button size="sm" onClick={() => setEditTab('details')}>Edit details</Button> : undefined}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Customer">{report.partyName ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Contact">{report.partyContact ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Site">{[report.partyAddress, report.city, report.state, report.pincode].filter(Boolean).join(', ') || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Visit type">{report.visitType ?? '—'}</Descriptions.Item>
                </Descriptions>
              </Card>

              <Card title="Visit details" style={{ marginBottom: 16 }}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Exit at">{report.exitAt ? formatIst(report.exitAt) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Return at">{report.returnAt ? formatIst(report.returnAt) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Travel mode">{report.travelMode ?? '—'}</Descriptions.Item>
                  {report.travelStartLoc && <Descriptions.Item label="From">{report.travelStartLoc}</Descriptions.Item>}
                  {report.travelEndLoc && <Descriptions.Item label="To">{report.travelEndLoc}</Descriptions.Item>}
                </Descriptions>
                {(report.workDone || report.observations || report.partyFeedback || report.notes) && (
                  <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                    {report.workDone && <Descriptions.Item label="Work done">{report.workDone}</Descriptions.Item>}
                    {report.observations && <Descriptions.Item label="Observations">{report.observations}</Descriptions.Item>}
                    {report.partyFeedback && <Descriptions.Item label="Party feedback">{report.partyFeedback}</Descriptions.Item>}
                    {report.notes && <Descriptions.Item label="Notes">{report.notes}</Descriptions.Item>}
                  </Descriptions>
                )}
              </Card>
            </>
          )}

          <Card title={`Expense items (${items.length})`} style={{ marginBottom: 16 }}
            extra={canEditItems ? <Button size="sm" variant="ghost" onClick={() => setEditTab('expenses')}>Edit expenses</Button> : undefined}>
            {items.length === 0 ? (
              <Typography.Text type="secondary">No expenses on this report.</Typography.Text>
            ) : (
              <Table
                rowKey="id" dataSource={items} pagination={false} size="small" layoutFixed columns={lineColumns}
                footer={() => (
                  <div className="flex items-center justify-between text-content">
                    <b>Subtotal</b>
                    <b>{inr2(report.subtotalAmount)}</b>
                  </div>
                )}
              />
            )}
          </Card>

          {/* Activity sits in the WIDE column: it is the longest block on the page, and with it on the
              right the left column ran out halfway down and left a screenful of empty card behind. */}
          <Card title="Activity" style={{ marginBottom: 16 }}>
            {activity.length ? <Timeline items={activity} /> : <Typography.Text type="secondary">No activity yet.</Typography.Text>}
          </Card>
        </Col>

        {/* Right: the money, and who decided what */}
        <Col xs={24} lg={9}>
          {/* Settlement, stated plainly. A REJECTED claim settles NOTHING — its columns are null, and
              rendering them as ₹0 would read as "settled, nothing owed" instead of "never settled". */}
          {/*
            * ONE MONEY CARD, TOP TO BOTTOM.
            *
            * "Settlement" and "Money" used to sit one above the other and print the same figure twice —
            * the approved amount appeared in both, under two different labels, which reads as two
            * different amounts that happen to match. There is only one story here and it runs in one
            * direction: what was spent → what was allowed → what the advance already covered → what is
            * actually paid back. Told once, as a stack, it also says WHICH BOOK pays: this page used to
            * assert "settled against the advance wallet" even for a claim paid out of collected retailer
            * cash, which never touches the wallet and reimburses nothing.
            */}
          <Card title="Money" style={{ marginBottom: 16 }}>
            {status === 'REJECTED' ? (
              <>
                <Typography.Text strong>Nothing settled — the claim was rejected</Typography.Text>
                {(report.managerRemarks || report.adminRemarks || report.correctionReason) && (
                  <div className="mt-1 text-sm text-muted">
                    Reason: {report.adminRemarks || report.managerRemarks || report.correctionReason}
                  </div>
                )}
                <div className="mt-2 text-sm text-muted">
                  Nothing was settled, so this claim leaves the rep’s wallet and collection cash unchanged.
                </div>
                <div className="mt-3 border-t border-line pt-1">
                  <AmountRow label="Claimed" value={inr2(report.subtotalAmount)} />
                </div>
              </>
            ) : (
              <div className="divide-y divide-line/70">
                <AmountRow label="Claimed" value={inr2(report.subtotalAmount)} />
                {report.paidAmount != null && (
                  <AmountRow
                    label="Approved"
                    value={inr2(report.paidAmount)}
                    tone={trimmed > 0 ? 'danger' : undefined}
                  />
                )}
                {trimmed > 0 && (
                  <AmountRow label="Trimmed by the approver" value={`− ${inr2(trimmed)}`} tone="danger" />
                )}
                {v2 && (
                  <>
                    <AmountRow label="From their wallet" value={`− ${inr2(v2.walletUsed)}`} />
                    <AmountRow label="From their collection cash" value={`− ${inr2(v2.collectionUsed)}`} />
                    <AmountRow
                      strong
                      label={v2.reimbursement?.status === 'PAID' ? 'Reimbursed' : Number(v2.reimbursementDue) > 0 ? 'To reimburse' : 'Reimbursement'}
                      value={Number(v2.reimbursementDue) > 0 ? inr2(v2.reimbursementDue) : 'Nothing owed'}
                    />
                  </>
                )}
                {!v2 && settlement && !cashFunded && (
                  <>
                    <AmountRow label="Met from their advance" value={`− ${inr2(settlement.advanceConsumed)}`} />
                    <AmountRow
                      strong
                      label={settlement.state === 'SETTLED' ? 'Reimbursed' : 'Still to reimburse'}
                      value={inr2(settlement.reimbursementDue)}
                    />
                    {Number(settlement.unusedAdvance) > 0 && (
                      <AmountRow label="Advance still with them" value={inr2(settlement.unusedAdvance)} />
                    )}
                  </>
                )}
                {!v2 && settlement && cashFunded && (
                  <AmountRow strong label="Reimbursed" value="Nothing — they spent collected cash" />
                )}
              </div>
            )}

            {v2 && (
              <div className="mt-2 space-y-1.5">
                <p className="type-caption text-muted">
                  Settled at approval ({v2.settlementNo}): wallet first, then collection cash{Number(v2.reimbursementDue) > 0 ? ', and the rest reimbursed' : ''}.

                  {v2.proofOverrideReason && ` Approved without required proof by a Super Admin: ${v2.proofOverrideReason}.`}
                </p>
                {/* THE REIMBURSEMENT AS A DOCUMENT — its number, where it stands and how it was paid. The amount is the row
                    above; this names the payment that amount is, so it can be found in the bank register and the log. */}
                {v2.reimbursement && (
                  <div className="rounded-control border border-line bg-surface-2 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="type-label-strong text-content">Reimbursement {v2.reimbursement.reimbursementNo}</span>
                      <Tag
                        tone={v2.reimbursement.status === 'PAID' ? 'green' : v2.reimbursement.status === 'OUTSTANDING' ? 'gold' : 'neutral'}
                        style={{ margin: 0 }}
                      >
                        {v2.reimbursement.status === 'PAID'
                          ? 'Paid'
                          : v2.reimbursement.status === 'OUTSTANDING'
                            ? 'Waiting for Accounts'
                            : String(v2.reimbursement.status ?? '').replace(/_/g, ' ').toLowerCase()}
                      </Tag>
                    </div>
                    <p className="mt-0.5 type-caption text-muted">
                      {v2.reimbursement.status === 'PAID'
                        ? `Paid ${formatIstDate(v2.reimbursement.paymentDate ?? v2.reimbursement.paidAt)} by ${String(v2.reimbursement.paymentMode ?? '').replace(/_/g, ' ').toLowerCase()}${v2.reimbursement.bankLabel ? ` from ${v2.reimbursement.bankLabel}${v2.reimbursement.bankLast4 ? ` ··${v2.reimbursement.bankLast4}` : ''}` : ''}${v2.reimbursement.paymentReference ? `, ref ${v2.reimbursement.paymentReference}` : ''}${v2.reimbursement.paidByName ? ` — recorded by ${v2.reimbursement.paidByName}` : ''}.`
                        : v2.reimbursement.status === 'OUTSTANDING'
                          ? 'Not paid yet — Accounts pays it from a company account. Their wallet is not touched.'
                          : 'Not paid — the approval it belonged to was reversed.'}
                    </p>
                    {v2.reimbursement.paymentProofUrl && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="type-label text-subtle">Payment proof</span>
                        <AttachmentCell files={[v2.reimbursement.paymentProofUrl]} label={`Payment proof · ${v2.reimbursement.reimbursementNo}`} size={36} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {openUnderV2 && status !== 'REJECTED' && (
              <p className="mt-2 type-caption text-muted">
                Nothing is taken from {whose} wallet until the claim is approved. Approval settles it from the wallet first,
                then the collection cash they hold, and anything left is reimbursed.
              </p>
            )}

            {/* NEW FLOAT GIVEN WITH THIS CLAIM — company advances, listed so nobody reads them as part of the settlement. */}
            {(report.walletTopUps ?? []).length > 0 && (
              <div className="mt-3 space-y-1 border-t border-line pt-3">
                <div className="type-overline text-subtle">Wallet top-ups given with this claim</div>
                {(report.walletTopUps ?? []).map((t: any) => (
                  <div key={t.id} className="flex items-start justify-between gap-3">
                    <span className="min-w-0 type-caption text-muted">
                      {t.txnNo} · {formatIstDate(t.txnDate)} · {String(t.paymentMode ?? '').replace(/_/g, ' ').toLowerCase()}
                      {t.bankLabel || t.bankName ? ` from ${t.bankLabel || t.bankName}${t.bankLast4 ? ` ··${t.bankLast4}` : ''}` : ''}
                      {t.paymentReference ? `, ref ${t.paymentReference}` : ''}
                      {t.recordedByName ? ` — by ${t.recordedByName}` : ''}
                      {!!t.reversed && ' · reversed'}
                    </span>
                    <span className={`shrink-0 tabular-nums text-sm ${t.reversed ? 'text-subtle line-through' : 'text-brand-700 dark:text-brand-300'}`}>+{inr2(t.amount)}</span>
                  </div>
                ))}
                <p className="type-caption text-subtle">New float in their wallet — separate from the reimbursement above.</p>
              </div>
            )}

            {/* MONEY OWED BACK — each reimbursement that had been paid before the claim was reversed, and whether it has
                come back. The payment itself is not undone; this is the separate debt it left. */}
            {recoveries.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-line pt-3">
                <div className="type-overline text-subtle">Owed back by {report.repName ?? 'the rep'}</div>
                {recoveries.map((rc) => {
                  const due = rc.status === 'OUTSTANDING';
                  return (
                    <div key={rc.id} className={due
                      ? 'rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2'
                      : 'rounded-control border border-line bg-surface-2 px-3 py-2'}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="type-label-strong text-content">{rc.recoveryNo}</span>
                        <span className={`tabular-nums ${due ? 'font-semibold text-warning-600 dark:text-harvest-300' : 'text-brand-700 dark:text-brand-300'}`}>{inr2(rc.amount)}</span>
                      </div>
                      <p className="mt-0.5 type-caption text-muted">
                        {rc.paidDocNo ? `${rc.paidDocNo} had been paid${rc.paidOn ? ` on ${formatIstDate(rc.paidOn)}` : ''}${rc.paidMode ? ` by ${String(rc.paidMode).replace(/_/g, ' ').toLowerCase()}` : ''}. ` : ''}
                        {rc.raisedPath === 'HUB_REVERT' ? 'Sent back' : 'Reopened'}{rc.raisedByName ? ` by ${rc.raisedByName}` : ''}{rc.raisedAt ? ` on ${formatIstDate(rc.raisedAt)}` : ''}: {rc.reason}
                      </p>
                      <p className={`mt-0.5 type-caption ${due ? 'text-warning-600 dark:text-harvest-300' : 'text-brand-700 dark:text-brand-300'}`}>
                        {due
                          ? 'Waiting to be received. Their wallet was not touched.'
                          : `Received ${rc.receiptDate ? formatIstDate(rc.receiptDate) : ''} by ${String(rc.receiptMode ?? '').replace(/_/g, ' ').toLowerCase()}${rc.bankLabel ? ` into ${rc.bankLabel}${rc.bankLast4 ? ` ··${rc.bankLast4}` : ''}` : ''}${rc.receiptReference ? `, ref ${rc.receiptReference}` : ''}${rc.receivedByName ? ` — recorded by ${rc.receivedByName}` : ''}.`}
                      </p>
                      {rc.receiptProofUrl && (
                        <div className="mt-1 flex items-center gap-2">
                          <span className="type-label text-subtle">Receipt proof</span>
                          <AttachmentCell files={[rc.receiptProofUrl]} label={`Receipt proof · ${rc.recoveryNo}`} size={36} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!v2 && !openUnderV2 && (<>
            <div className="mt-3 flex items-center gap-2">
              <span className="type-label text-subtle">Paid for with</span>
              <Tag tone={cashFunded ? 'gold' : 'blue'} style={{ margin: 0 }}>
                {cashFunded ? 'Collection money' : 'Company wallet / float'}
              </Tag>
            </div>

            <p className="mt-1.5 type-caption text-muted">
              {cashFunded
                ? 'Paid out of retailer cash this person collected and had not yet banked. Nothing is reimbursed — approving reduces what they still owe the company by the approved amount.'
                : 'Drawn from their company float. Submitting debits the wallet, and anything over the float is reimbursed when the claim is settled.'}
            </p>
            </>)}

            {/* An action, so it looks like one: a bordered chip naming its destination, rather than a
                bare phrase that reads as part of the paragraph above it. */}
            <button
              type="button"
              /*
               * BOTH DESTINATIONS NOW MATCH THEIR LABEL.
               *
               * WALLET: "View this person's wallet" opened `/wallets` — the entire field-staff register
               * — and left the reader to find them in it. `/wallets?focus=<repId>` opens that rep's own
               * wallet instead.
               *
               * COLLECTION MONEY: this pointed at `/staff/:id?tab=money`, and that page has no tabs, so
               * the parameter did nothing. The obvious-looking alternative is worse:
               * `/billing/payments?mode=CASH&receivedBy=<repId>` is the link the staff money card uses,
               * but **`receivedBy` is honoured by nothing** — PaymentsListPage reads only tab/status/
               * mode/bank/q/retailer — so it lands on EVERY custodian's cash, under a button naming one
               * person. `RepCashPanel`, the per-rep view, is currently imported by nobody.
               *
               * So the destination is the person's own page: `/staff/<repId>` renders `StaffMoneyCard`,
               * which reads `/retailer-money/staff/:repId` — collected minus deposited, for THEM.
               */
              onClick={() => navigate(
                cashFunded
                  ? `/staff/${report.repId}`
                  : `/wallets?focus=${report.repId}`,
              )}
              className="mt-2 inline-flex items-center gap-1.5 rounded-control border border-line bg-surface-2 px-2.5 py-1 type-caption font-medium text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-500/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-400"
            >
              {cashFunded ? <HandCoins className="size-3.5" /> : <Wallet className="size-3.5" />}
              {/* NAME THE PERSON. "their" / "this person's" left the reader to work out whose money
                  they were about to open — on a page that may have been reached from a queue of other
                  people's claims, that is the one thing the label has to say. */}
              {cashFunded ? `View ${whose} collection money` : `View ${whose} wallet`}
              <ArrowRight className="size-3 opacity-70" />
            </button>
          </Card>

          <Card title="Decisions" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Submitted">{submittedCell}</Descriptions.Item>
              <Descriptions.Item label={status === 'REJECTED' ? 'Rejected' : 'Approved'}>{approvedCell}</Descriptions.Item>
            </Descriptions>
          </Card>

        </Col>
      </Row>

      {/* ── Reason modal (send-back / reject / reopen) ── */}
      <Modal
        open={!!reasonModal}
        onOpenChange={(o) => { if (!o) { setReasonModal(null); setReason(''); } }}
        title={reasonModal?.title}
        footer={
          <>
            <Button variant="default" onClick={() => { setReasonModal(null); setReason(''); }}>Cancel</Button>
            <Button
              variant={reasonModal?.url.includes('reject') ? 'danger' : 'primary'}
              disabled={!reason.trim()}
              loading={action.isPending}
              onClick={() => {
                if (!reasonModal) return;
                const isReopen = reasonModal.url === 'reopen';
                action.mutate(
                  { url: reasonModal.url, data: { [reasonModal.field]: reason.trim() } },
                  {
                    onSuccess: (res: any) => {
                      setReasonModal(null); setReason('');
                      // Reopening hands the claim back to the rep as a DRAFT, and a draft is private to
                      // its owner — so an approver who stayed on this page would immediately hit
                      // "couldn't load this report". Return them to the list instead — unless money is now
                      // owed back on it, which keeps the claim open to Accounts to record it coming in.
                      if (isReopen && !isFiler && !(Number(res?.recoveryDue ?? 0) > 0)) navigate('/expense-reports');
                    },
                  },
                );
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (visible to the sales person)"
        />
      </Modal>

      {/* ── Approve: the one settlement dialog every approval uses (wallet → collection → reimbursement) ── */}
      {adminOpen && <SettlementApproveDialog reportId={id} onClose={() => setAdminOpen(false)} />}

      {/* ── Pay the reimbursement an approval left owing (legacy claims open the old payout dialog) ── */}
      {payOpen && <PayReimbursementDialog reportId={id} onClose={() => setPayOpen(false)} />}

      {/* ── Record money owed back on a reversed, already-reimbursed claim ── */}
      {recoveryOpen && <RecordRecoveryDialog reportId={id} onClose={() => setRecoveryOpen(false)} />}

      {/* ── New float for the rep, separate from the reimbursement ── */}
      {topUpOpen && <WalletTopUpDialog reportId={id} onClose={() => setTopUpOpen(false)} />}

      {/* ── Amount-mismatch verify modal ── */}
      <Modal
        open={!!mismatchItem}
        onOpenChange={(o) => { if (!o) setMismatchItem(null); }}
        title="Flag amount mismatch"
        footer={
          <>
            <Button variant="default" onClick={() => setMismatchItem(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!verifiedAmount || verifiedAmount <= 0}
              loading={verifyItem.isPending}
              onClick={() => {
                if (!mismatchItem) return;
                verifyItem.mutate({
                  itemId: String(mismatchItem.id),
                  data: {
                    status: 'AMOUNT_MISMATCH',
                    verifiedAmount: Number(verifiedAmount),
                    ...(mismatchReason.trim() ? { rejectionReason: mismatchReason.trim() } : {}),
                  },
                });
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography.Text type="secondary">
            Claimed amount: <b>{inr2(mismatchItem?.amount)}</b>
          </Typography.Text>
          <div>
            <Typography.Text type="secondary">Verified amount (₹)</Typography.Text>
            <InputNumber
              style={{ width: '100%' }} min={1} value={verifiedAmount}
              onChange={(v) => setVerifiedAmount(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <Typography.Text type="secondary">Reason (optional)</Typography.Text>
            <Input.TextArea rows={2} value={mismatchReason} onChange={(e) => setMismatchReason(e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* A DAY claim is edited in the day sheet — where its lines, their proof and "Submit day" live. The
          per-visit editor demands a visit type a day claim does not have, so a sent-back day claim could
          never be saved there. (Only for the rep themself: the day sheet loads the CALLER's day.) */}
      {editTab && isDayClaim && isOwner && (
        <DayExpenseModal
          date={String(report.expenseDate).slice(0, 10)}
          onClose={() => setEditTab(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['expense-report', id] }); qc.invalidateQueries({ queryKey: ['expense-reports'] }); }}
        />
      )}
      {editTab && !(isDayClaim && isOwner) && (
        <EditReportModal
          reportId={id} initialTab={editTab} onClose={() => setEditTab(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['expense-report', id] }); qc.invalidateQueries({ queryKey: ['expense-reports'] }); }}
        />
      )}
    </div>
  );
}
